import React from "react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import { ArrowUpRight, Brain, Database, Code2, Layers, Sparkles, Linkedin } from "lucide-react";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/c28b8cc2-4cf8-473c-a4b0-afedaa476fee/images/4b9195bdc13f4eb652caf33831a601c8c44565fa21fb8e625942dfe510133932.png";
const TESTI_BG = "https://static.prod-images.emergentagent.com/jobs/c28b8cc2-4cf8-473c-a4b0-afedaa476fee/images/ffe852a3829e066eb1e11655dfc6ba70d5bfeee00451ec37eb6f62d6102908fb.png";

const LINKEDIN_JOBS_URL = "https://www.linkedin.com/company/switchtt/jobs";

const CLIENT_LOGOS = [
  { name: "ATG Entertainment", src: "/clients/atg.png" },
  { name: "Land App", src: "/clients/landapp.png" },
  { name: "Polaris", src: "/clients/polaris.png" },
  { name: "Toloka", src: "/clients/toloka.png" },
  { name: "Greystar", src: "/clients/greystar.png" },
  { name: "DMG Media", src: "/clients/dmg.png" },
];

const SECTORS = [
  { title: "AI & ML", icon: Brain, blurb: "Foundation models, applied research, MLOps. We place builders at the frontier." },
  { title: "Data", icon: Database, blurb: "Platform, engineering, analytics. From lakehouse to semantic layer." },
  { title: "Software", icon: Code2, blurb: "Backend, full-stack, platform, DevOps. Shipping at scale across the stack." },
  { title: "Product", icon: Layers, blurb: "PMs and Designers who turn ambiguity into category-defining products." },
];

const TESTIMONIALS = [
  { quote: "Their screening, accurate candidate profiling and management is one of the best I've experienced. Their tenacity improves our pipeline and time to hire. I'd recommend Switch TT for tech staffing.", who: "VP of Operations, Argus Media" },
  { quote: "The whole team at Switch feel like part of our internal team. They provide a service tailored to our needs with the candidate's experience at the heart of what they do.", who: "Talent Manager, ROLI" },
  { quote: "Scott's precise understanding of our needs, diligent candidate sourcing and effective communication made the recruitment process smooth. I strongly recommend Switch Tech Talent.", who: "Head of Engineering, Liquid Barcodes" },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden pt-24">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="network" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black" />
          <div className="absolute inset-0 grid-bg opacity-40" />
        </div>

        <div className="relative z-10 max-w-7xl w-full mx-auto px-6 md:px-12 pb-12">
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-display font-black text-white text-4xl sm:text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.9] max-w-5xl"
            data-testid="hero-headline"
          >
            Finding, connecting<br />&amp; scaling tech teams<br />across the UK, Europe &amp; US.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="mt-8 text-white/70 max-w-2xl text-base md:text-lg font-body leading-relaxed"
          >
            A specialist partner for ambitious organisations hiring in AI, Data, Software and Product. From Series A scale-ups to category-defining enterprises, we build the teams that build the future.
          </motion.p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a href={LINKEDIN_JOBS_URL} target="_blank" rel="noreferrer" data-testid="hero-cta-find-roles" className="btn-volt px-7 py-4 text-xs uppercase tracking-[0.18em] inline-flex items-center gap-2">
              View live roles <ArrowUpRight size={16} />
            </a>
            <Link to="/contact" data-testid="hero-cta-hire-talent" className="btn-outline-light px-7 py-4 text-xs uppercase tracking-[0.18em] inline-flex items-center gap-2">
              Hire talent <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>

        <div className="relative z-10 pb-8" />
      </section>

      {/* CLIENT LOGOS */}
      <section className="border-y border-white/10 py-12 bg-black overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 mb-8">
          <div className="font-mono text-[10px] text-white/40 uppercase tracking-[0.3em]">Brands we partner with</div>
        </div>
        <Marquee gradient={true} gradientColor="#000000" gradientWidth={120} speed={40} pauseOnHover>
          {CLIENT_LOGOS.concat(CLIENT_LOGOS).map((c, i) => (
            <div key={`${c.name}-${i}`} className="mx-12 md:mx-16 flex items-center justify-center h-12 md:h-14">
              <img
                src={c.src}
                alt={c.name}
                className="h-full w-auto object-contain opacity-60 hover:opacity-100 transition-opacity"
                data-testid={`client-logo-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
              />
            </div>
          ))}
        </Marquee>
      </section>

      {/* STATS */}
      <section className="py-20 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-0">
          {[
            { k: "Countries", v: "12+" },
            { k: "Placements / yr", v: "100+" },
            { k: "Avg. Time to Hire", v: "28d" },
            { k: "Vetted Tech Professionals", v: "15,000+" },
          ].map((s, i) => (
            <div key={s.k} className={`p-8 ${i !== 3 ? "md:border-r border-white/10" : ""} ${i < 2 ? "border-b md:border-b-0 border-white/10" : ""} ${i % 2 === 0 ? "border-r md:border-r" : ""}`}>
              <div className="font-display font-black text-5xl md:text-6xl tracking-tighter text-white">{s.v}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 mt-3">{s.k}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SECTORS */}
      <section id="sectors" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
            <div className="md:col-span-4" />
            <div className="md:col-span-8">
              <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter text-white leading-[0.95]">
                Four disciplines.<br />One obsession: <span className="text-[#3B82F6]">craft</span>.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-white/10">
            {SECTORS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.title}
                  data-testid={`sector-card-${s.title.toLowerCase().replace(/\s+|&/g, "-")}`}
                  className={`relative p-8 md:p-12 min-h-[280px] flex flex-col justify-between border-white/10 ${i % 2 === 0 ? "md:border-r" : ""} ${i < 2 ? "border-b" : ""}`}
                >
                  <div>
                    <div className="w-12 h-12 border border-white/20 flex items-center justify-center text-[#60D8FF] mb-8">
                      <Icon size={22} />
                    </div>
                    <h3 className="font-display font-bold text-4xl md:text-5xl tracking-tighter text-white mb-4">{s.title}</h3>
                    <p className="text-white/60 text-sm md:text-base max-w-md leading-relaxed font-body">{s.blurb}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* LIVE ROLES CTA (simple — links to LinkedIn) */}
      <section className="py-24 border-t border-white/10 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter text-white leading-[0.95]">
              All our live roles live on LinkedIn.
            </h2>
            <p className="mt-6 text-white/60 font-body text-base md:text-lg max-w-xl leading-relaxed">
              Browse current opportunities, filter by specialism and apply directly through our company page.
            </p>
          </div>
          <div className="md:col-span-5 md:text-right">
            <a
              href={LINKEDIN_JOBS_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="home-view-linkedin-jobs"
              className="inline-flex items-center gap-3 btn-volt px-8 py-5 text-xs uppercase tracking-[0.2em]"
            >
              <Linkedin size={18} /> View live roles <ArrowUpRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="relative py-24 md:py-32 border-t border-white/10 overflow-hidden">
        <img src={TESTI_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-10" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="font-mono text-[10px] text-[#3B82F6] uppercase tracking-[0.3em] mb-16">What our clients say</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-white/10 bg-black/40 backdrop-blur-sm">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className={`p-10 ${i !== 2 ? "md:border-r border-white/10" : ""} ${i !== 2 ? "border-b md:border-b-0 border-white/10" : ""}`} data-testid={`testimonial-${i}`}>
                <Sparkles className="text-[#3B82F6] mb-6" size={20} />
                <p className="text-white/90 text-base md:text-lg leading-relaxed font-body mb-8">"{t.quote}"</p>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">{t.who}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#083e7f] text-white relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <h2 className="font-display font-black text-5xl md:text-7xl tracking-tighter leading-[0.9]">
              Building careers and growing teams with the right connections.
            </h2>
          </div>
          <div className="md:col-span-4 flex flex-col gap-4 md:items-end">
            <Link to="/contact" data-testid="cta-hire" className="inline-flex items-center gap-2 bg-white text-[#083e7f] px-7 py-4 text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#3B82F6] hover:text-white transition-colors">
              Hire talent <ArrowUpRight size={16} />
            </Link>
            <a href={LINKEDIN_JOBS_URL} target="_blank" rel="noreferrer" data-testid="cta-apply" className="inline-flex items-center gap-2 border-2 border-white px-7 py-4 text-xs uppercase tracking-[0.2em] font-bold hover:bg-white hover:text-[#083e7f] transition-colors">
              View live roles <ArrowUpRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
